import { useCallback } from "react";
import type { Command } from "../types";

type Params = {
  apiBaseUrl: string;
  /** stable token getter — see useFreshToken */
  getToken: () => Promise<string | null>;
  headsetId: string | null;
};

/**
 * Fire-and-forget command posting. Nothing here inspects the response: the headset
 * answers by changing its vr.state, which arrives over the socket, so the UI is
 * driven by the state rather than by the POST.
 *
 * `to` overrides the selected headset, for the one caller that has to address a
 * headset in the same handler that selects it, before the selection has rendered.
 */
export function useHeadsetCommands({ apiBaseUrl, getToken, headsetId }: Params) {
  return useCallback(
    async (cmd: Command, to?: string) => {
      const id = (to ?? headsetId ?? "").trim();
      if (!id) return;

      const token = await getToken();
      if (!token) return;

      // Callers fire and forget, so a failed POST must not reject: it would surface
      // as an unhandled rejection. The UI simply sees no state change.
      try {
        await fetch(`${apiBaseUrl}/headsets/${encodeURIComponent(id)}/command`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ cmd }),
        });
      } catch (err) {
        console.error(`headset command "${cmd}" failed`, err);
      }
    },
    [apiBaseUrl, getToken, headsetId]
  );
}
